import { Component, inject, Input, ChangeDetectionStrategy } from '@angular/core'
import { Meeting, MeetingProcessingStatus, MeetingsService, isDraftMeeting } from '../meetings.service'
import { CommonModule, NgOptimizedImage } from '@angular/common'
import { MatIconButton } from '@angular/material/button'
import { MatChip } from '@angular/material/chips'
import { MatIcon } from '@angular/material/icon'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { ProgressLoaderComponent } from '../common/progress-loader/progress-loader.component'
import { MatTooltip } from '@angular/material/tooltip'
import { RouterLink } from '@angular/router'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { DurationPipe } from '../duration.pipe'
import { MatDialog } from '@angular/material/dialog'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import { formatCounterpartyNames } from '../utils'
import { ToastService } from '../toast.service'

@Component({
  selector: 'app-previous-meeting-card',
  standalone: true,
  imports: [
    CommonModule,
    MatChip,
    NgOptimizedImage,
    RouterLink,
    MatIcon,
    MatIconButton,
    TranslatePipe,
    TranslateDirective,
    MatTooltip,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    ProgressLoaderComponent,
    DurationPipe,
  ],
  templateUrl: './previous-meeting-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './previous-meeting-card.component.scss',
})
export class PreviousMeetingCardComponent {
  @Input({ required: true })
  meeting!: Meeting

  @Input()
  activeMatch: string | null = null

  @Input({ required: true })
  isActive!: boolean

  @Input()
  useCompactMode: boolean = false

  meetingsService = inject(MeetingsService)
  dialog = inject(MatDialog)
  translateService = inject(TranslateService)
  deleteMeetingMutation = this.meetingsService.deleteMeetingMutation()
  toggleNotificationSettingMutation = this.meetingsService.toggleNotificationSetting()
  toastService = inject(ToastService)

  onMenuClick(event: Event) {
    event.preventDefault()
    event.stopPropagation()
  }

  async toggleNotificationSetting(event: Event) {
    event.preventDefault()
    event.stopPropagation()
    const notifyWhenPostProcessingCompletes = !this.meeting.notifyWhenPostProcessingCompletes
    await this.toggleNotificationSettingMutation.mutateAsync({
      meetingId: this.meeting.id,
      notifyWhenPostProcessingCompletes,
    })
    if (notifyWhenPostProcessingCompletes) {
      this.toastService.show('MEETINGS.NOTIFICATIONS.ENABLED_TOAST')
    } else {
      this.toastService.show('MEETINGS.NOTIFICATIONS.DISABLED_TOAST')
    }
  }

  delete(meeting: Meeting) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('MEETINGS.DELETE.DIALOG_TITLE'),
        description: this.translateService.instant('MEETINGS.DELETE.DIALOG_BODY', { meetingName: meeting.name }),
        descriptionClass: 'mask-meeting-name',
        confirmLabel: this.translateService.instant('COMMON.YES'),
      },
    })
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.deleteMeetingMutation.mutateAsync(meeting.id)
      }
    })
  }

  get formattedCounterpartyNames(): string {
    return formatCounterpartyNames(this.meeting.counterpartyNames)
  }

  getMatchLeft(meeting: Meeting, startTimeSeconds: number): number {
    return (startTimeSeconds / meeting.duration!) * 100
  }

  get isSplitting(): boolean {
    return (
      this.meeting.totalNumberOfSplits > 0 &&
      (this.meeting.processingStatus === MeetingProcessingStatus.SPLITTING_MEETING ||
        this.meeting.processingStatus === MeetingProcessingStatus.PROCESSING_MEDIA)
    )
  }

  get isDraft(): boolean {
    return isDraftMeeting(this.meeting)
  }
}
